/**
 * A visual mark for each framework.
 *
 * WHY EACH ONE IS DIFFERENT
 * A wall of identical cards makes a customer read seven titles to find the one
 * they want. A distinct mark lets them find NIS2 by shape before they read a
 * word, and after a week they stop reading the titles at all. So each glyph
 * depicts the framework's SUBJECT rather than being a generic shield:
 * networked systems for NIS2, institutional columns for DORA, a certification
 * seal for ISO 27001, a person for GDPR, a chip for the AI Act, the six
 * Functions for NIST CSF, a product for the CRA.
 *
 * COLOUR COMES FROM THE CATEGORICAL SERIES, NEVER THE STATUS RAMP
 * `--status-good/warning/serious/critical` mean a compliance verdict everywhere
 * else in this product. Borrowing red for "the CRA" would make a framework card
 * look like a failing one. The categorical palette exists for exactly this —
 * identity without meaning — and is already colour-vision-deficiency checked.
 *
 * Assignment is by key, not by position, so adding a framework never silently
 * recolours the others. An unknown key falls back to a neutral mark rather than
 * guessing, because a wrong identity is worse than a plain one.
 */

import type { ReactElement, SVGProps } from 'react';

const base = (props: SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

/** Networked systems — NIS2 is about network and information systems. */
const MarkNis2 = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="5" r="2.2" /><circle cx="5" cy="17" r="2.2" /><circle cx="19" cy="17" r="2.2" />
    <path d="M10.4 6.8 6.6 15.2M13.6 6.8l3.8 8.4M7.2 17h9.6" />
  </svg>
);

/** Columns — DORA governs financial institutions' operational resilience. */
const MarkDora = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M3 9.5 12 4l9 5.5" /><path d="M4.5 20h15" />
    <path d="M6.5 11.5V18M10 11.5V18M14 11.5V18M17.5 11.5V18" />
  </svg>
);

/** A certification seal — ISO 27001 is the one you get certified against. */
const MarkIso = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="9.5" r="5.5" /><path d="m9.8 9.6 1.6 1.6 3-3.2" />
    <path d="M8.4 14.2 7 21l5-2.2L17 21l-1.4-6.8" />
  </svg>
);

/** A person behind a shield — GDPR protects people, not systems. */
const MarkGdpr = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 3 5 5.6v5.2c0 4.2 2.9 8 7 9.2 4.1-1.2 7-5 7-9.2V5.6Z" />
    <circle cx="12" cy="10" r="1.9" /><path d="M8.8 15.6a3.6 3.6 0 0 1 6.4 0" />
  </svg>
);

/** A chip — the AI Act regulates AI systems placed on the market. */
const MarkAiAct = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="7.5" y="7.5" width="9" height="9" rx="1.6" /><rect x="10.6" y="10.6" width="2.8" height="2.8" rx="0.6" />
    <path d="M10 7.5V5M14 7.5V5M10 19v-2.5M14 19v-2.5M7.5 10H5M7.5 14H5M19 10h-2.5M19 14h-2.5" />
  </svg>
);

/** Six segments — the six Functions: Govern, Identify, Protect, Detect, Respond, Recover. */
const MarkNistCsf = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.6" />
    <path d="M12 4v5.4M20 12h-5.4M16 18.9l-2.7-4.6M8 18.9l2.7-4.6M4 12h5.4" />
  </svg>
);

/** A product with a chip — the CRA covers products with digital elements. */
const MarkCra = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M3.5 7.8 12 3.6l8.5 4.2v8.4L12 20.4l-8.5-4.2Z" /><path d="M3.5 7.8 12 12l8.5-4.2M12 12v8.4" />
    <rect x="9.6" y="9.1" width="4.8" height="4.2" rx="0.8" />
  </svg>
);

/** A certified shield with EU stars — the Cybersecurity Act's EUCC certification. */
const MarkCsaEucc = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 3 5 5.4v5.6c0 4.3 2.9 7.9 7 9 4.1-1.1 7-4.7 7-9V5.4Z" />
    <path d="m9 11.4 2 2 4-4.4" />
    <path d="M12 16.4v.02M8.9 15.1v.02M15.1 15.1v.02" />
  </svg>
);

/** A checklist with a ladder tick — CIS Controls are a prioritised safeguard list. */
const MarkCis = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="4" y="3.5" width="16" height="17" rx="2.2" />
    <path d="m7.6 8.6 1.3 1.3 2.2-2.4M7.6 14.4l1.3 1.3 2.2-2.4M13.5 8.7H17M13.5 14.5H17" />
  </svg>
);

/** Fallback: layered documents. Used when we do not recognise the key. */
const MarkGeneric = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 3 3 7.5l9 4.5 9-4.5Z" /><path d="M3 12.5 12 17l9-4.5" /><path d="M3 17 12 21.5 21 17" />
  </svg>
);

/**
 * key -> [icon, categorical slot].
 *
 * Slots are chosen so neighbours in the grid never collide: the cards render
 * three-up, so 1/2/3 on the first row and 5/7/6 on the second keeps adjacent
 * marks distinguishable. Slot 8 (red) is deliberately unused — in this product
 * red reads as a failure, whatever the palette says it is.
 */
const MARKS: Record<string, [(p: SVGProps<SVGSVGElement>) => ReactElement, number]> = {
  nis2: [MarkNis2, 1],
  dora: [MarkDora, 2],
  iso27001: [MarkIso, 3],
  gdpr: [MarkGdpr, 5],
  aiact: [MarkAiAct, 7],
  nistcsf: [MarkNistCsf, 6],
  cra: [MarkCra, 4],
  // CIS shares magenta with GDPR; the two are never adjacent in a grid.
  'cis-ig1': [MarkCis, 5],
  // EUCC readiness is the last card (sortOrder 9, bottom-right in the three-up
  // grid). Its grid neighbours are cis-ig1 (slot 5) and NIST CSF (slot 6), so it
  // reuses DORA's slot 2 — never adjacent to it — rather than green (ISO) or
  // teal (CRA), where a shared colour beside a sibling framework could read as
  // the same one. The glyph is a certification shield, which carries the identity.
  'csa-eucc': [MarkCsaEucc, 2],
};

export function frameworkColorVar(key: string): string {
  const slot = MARKS[key]?.[1];
  return slot ? `var(--series-${slot})` : 'var(--text-muted)';
}

/**
 * The mark itself: a tinted tile with the glyph in the framework's colour.
 *
 * `aria-hidden` throughout — the framework name sits beside it in every use,
 * and announcing "NIS2 icon, NIS2" is noise to a screen-reader user.
 */
export function FrameworkMark({ frameworkKey, size = 'md', className }: {
  frameworkKey: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const [Glyph] = MARKS[frameworkKey] ?? [MarkGeneric, 0];
  const color = frameworkColorVar(frameworkKey);
  const box = size === 'sm' ? 'w-9 h-9 rounded-lg' : 'w-11 h-11 rounded-xl';
  const glyph = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';

  return (
    <span
      aria-hidden
      className={`${box} shrink-0 grid place-items-center ${className ?? ''}`}
      style={{
        // Tint rather than a solid fill: a row of saturated tiles competes with
        // the primary action, which is the button.
        background: `rgb(${color} / 0.12)`,
        color: `rgb(${color})`,
      }}
    >
      <Glyph className={glyph} />
    </span>
  );
}
